import { useCallback, useEffect, useState } from 'react';
import { disablePushDevice, fetchPushStatus } from '../hooks/api/pushNotifications';
import { getPsychologistId } from '../hooks/api/auth/tokenManager';
import { isPushSessionRestricted } from './foreignSession';
import { isPushConfigured } from './firebaseConfig';
import {
  activatePushOnDevice,
  browserSupportsPush,
  detectPlatform,
  getDeviceToken,
  loadMessaging,
  notifyPushChanged,
  readIntent,
  registerThisDevice,
  writeIntent,
} from './pushDevice';

export { detectPlatform };

export type PushUiState =
  | 'loading'
  | 'unconfigured' // faltan la config web / la clave VAPID de Firebase
  | 'unsupported' // el navegador/dispositivo no soporta push
  | 'impersonated' // sesión de otra persona (admin): no se activan push, registrarían el celular del admin como el de ella
  | 'denied' // la psicóloga bloqueó el permiso (solo se cambia desde los ajustes del dispositivo)
  | 'off'
  | 'on'
  | 'working';

/**
 * Avisa al backend que la app se abrió en este dispositivo (refresca `last_seen_at`). Solo si ELLA las había activado acá
 * (permiso dado + intención guardada) Y el backend sigue teniendo este dispositivo activo: nunca activa nada por su
 * cuenta ni "recupera" un dispositivo que se reemplazó o desconectó (con dos celulares de la misma plataforma
 * se pisarían entre sí en cada apertura). Reactivar es cosa de la pantalla Notificaciones. Nunca tira.
 */
export async function refreshPushRegistration(): Promise<void> {
  try {
    if (isPushSessionRestricted()) return;
    if (!isPushConfigured() || !browserSupportsPush() || Notification.permission !== 'granted') return;
    const psychologistId = getPsychologistId();
    if (!psychologistId || readIntent() !== psychologistId) return;
    const token = await getDeviceToken();
    const status = await fetchPushStatus(token);
    if (!status.thisDeviceEnabled) return;
    await registerThisDevice(token);
  } catch {
    /* sin red o sin service worker todavía: se reintenta la próxima vez que se abra la app */
  }
}

/**
 * Estado y acciones de las push notifications de psiplatform mobile para la psicóloga logueada.
 * El token FCM se asocia a ELLA en el backend; solo recibe push quien las activó.
 */
export function usePushNotifications() {
  const [state, setState] = useState<PushUiState>('loading');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const set = (next: PushUiState) => {
      if (!cancelled) setState(next);
    };

    (async () => {
      if (isPushSessionRestricted()) return set('impersonated');
      if (!isPushConfigured()) return set('unconfigured');
      if (!browserSupportsPush()) return set('unsupported');
      if (Notification.permission === 'denied') return set('denied');
      if (Notification.permission === 'default') return set('off');

      try {
        const token = await getDeviceToken();
        const status = await fetchPushStatus(token);
        if (status.thisDeviceEnabled) return set('on');

        // Permiso dado y ELLA las había activado acá, pero el backend no tiene este token: rotó.
        const psychologistId = getPsychologistId();
        if (psychologistId && readIntent() === psychologistId) {
          await registerThisDevice(token);
          return set('on');
        }
        set('off');
      } catch {
        set('off');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const enable = useCallback(async () => {
    setError(null);
    setState('working');
    try {
      setState(await activatePushOnDevice());
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No pudimos activar las notificaciones.');
      setState('off');
    }
  }, []);

  const disable = useCallback(async () => {
    setError(null);
    setState('working');
    // La intención se borra PRIMERO: así un aviso de "app abierta" que corra a la vez no vuelve a registrar el dispositivo.
    const previousIntent = readIntent();
    writeIntent(null);
    try {
      const token = await getDeviceToken();
      await disablePushDevice(token);
      const { messaging, sdk } = await loadMessaging();
      await sdk.deleteToken(messaging);
      setState('off');
      notifyPushChanged();
    } catch (e) {
      writeIntent(previousIntent);
      setError(e instanceof Error ? e.message : 'No pudimos desactivar las notificaciones.');
      setState('on');
    }
  }, []);

  return { state, error, enable, disable };
}
